import posthog from "posthog-js"

import { isMarketingPage } from "@/helpers/marketing"
import { parseUrl } from "@/lib/url-parser"
// Shared with the Sentry browser SDK so analytics and error events land under
// the same environment tag.
import { currentEnvironment } from "@/lib/utils"

// Type guard for PostHog instance
function isPostHogInstance(obj: unknown): obj is typeof posthog {
  return obj !== null && typeof obj === "object" && "capture" in obj
}

// Promise that resolves when PostHog is loaded
let posthogReadyPromise: Promise<typeof posthog> | null = null
let posthogResolve: ((value: typeof posthog) => void) | null = null
let posthogTimeoutId: ReturnType<typeof setTimeout> | null = null

// Get a Promise that resolves when PostHog is ready.
//
// Everything here used to be built on `window.posthog`: a fast path, an
// init check, and a 50x100ms polling fallback. An ES-module import never
// assigns that global, so the fast path and the poll were dead — and the
// poll was doubly unreachable, since initializePostHog() always assigns
// posthogReadyPromise before returning. The promise is resolved by
// posthog.init's `loaded` callback, which is the only mechanism that ever
// worked, so that is all this keeps.
export function getPostHogReady(): Promise<typeof posthog> {
  if (posthogReadyPromise) return posthogReadyPromise

  initializePostHog()

  // initializePostHog always assigns the promise (including a rejected one
  // when there is no API key), so this is only a type-level fallback.
  return (
    posthogReadyPromise ??
    Promise.reject(new Error("PostHog failed to initialize"))
  )
}

// A rejected promise is cached like any other, so without this one
// transient failure (missing key, init throw, loaded-callback timeout)
// would silently drop every product event for the rest of the session.
// Clearing it lets the next capture retry.
function clearFailedReadyPromise() {
  posthogReadyPromise = null
  posthogResolve = null
  if (posthogTimeoutId) {
    clearTimeout(posthogTimeoutId)
    posthogTimeoutId = null
  }
}

// Initialize PostHog
export function initializePostHog() {
  // Prevent duplicate initialization. This guard used to test
  // `window.posthog`, which an ES-module import never assigns — so it was
  // never true, and entrypoints/application.ts:42 calls this on EVERY
  // `turbo:load`. posthog.init() therefore re-ran on every Turbo
  // navigation across the listener experience, and setupNavigationTracking
  // registered a fresh Inertia `router.on("success")` handler each time.
  // Module state is the thing that actually exists.
  if (posthogReadyPromise) {
    if (import.meta.env.DEV) {
      console.log("PostHog already initialized, skipping")
    }
    return
  }

  // Create Promise that will resolve in the loaded callback
  // Only create if one doesn't already exist (e.g., from getPostHogReady being called first)
  posthogReadyPromise ??= new Promise((resolve, reject) => {
    posthogResolve = resolve
    // Add timeout to prevent promise from hanging forever if loaded callback never fires
    // (e.g., due to ad blockers, network errors, etc.)
    posthogTimeoutId = setTimeout(() => {
      if (posthogResolve) {
        // Timeout occurred before loaded callback fired
        posthogResolve = null
        posthogTimeoutId = null
        clearFailedReadyPromise()
        reject(
          new Error(
            "PostHog initialization timeout - loaded callback never fired",
          ),
        )
      }
    }, 5000)
  })

  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY as string

  // Only log in development to avoid sensitive data exposure
  if (import.meta.env.DEV) {
    console.log("PostHog initialization attempt:", {
      hasApiKey: !!apiKey,
      keyLength: apiKey ? apiKey.length : 0,
    })
  }

  if (!apiKey) {
    console.warn("PostHog API key not found. Analytics will be disabled.")
    // Reject any pending Promise since PostHog won't initialize
    if (posthogResolve) {
      // Clear timeout since we're rejecting early
      if (posthogTimeoutId) {
        clearTimeout(posthogTimeoutId)
        posthogTimeoutId = null
      }
      // We can't reject an already-created Promise directly, but we can clear the resolve function
      posthogResolve = null
    }
    // Mark that PostHog can't be initialized
    if (posthogReadyPromise !== null) {
      // Replace with a rejected Promise to prevent hanging
      posthogReadyPromise = Promise.reject(
        new Error("PostHog API key not found. Analytics disabled."),
      )
    }
    return
  }

  // Check for GDPR consent (with safe localStorage access)
  let consent: string | null = null
  let hasConsent = false

  try {
    if (typeof window !== "undefined") {
      consent = localStorage.getItem("posthog_consent")
      hasConsent = consent === "true"
    }
  } catch (error) {
    // Handle localStorage access errors (private browsing, etc.)
    if (import.meta.env.DEV) {
      console.warn("Unable to access localStorage for consent:", error)
    }
  }

  if (!hasConsent && consent !== "false") {
    // No consent decision yet, initialize with opt-out
    if (import.meta.env.DEV) {
      console.log("No consent decision yet, initializing PostHog with opt-out")
    }
  }

  // Check if we're on a marketing page (for logging purposes only)
  const isMarketing = isMarketingPage()

  // Wrap posthog.init() in try-catch to handle initialization errors
  // If init() throws, reject the promise to prevent permanent hang
  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    posthog.init(apiKey, {
      api_host: "https://eu.posthog.com",
      // NOTE: We do NOT use disable_surveys here because it completely disables the
      // surveys module, including programmatic displaySurvey() calls. This breaks the
      // Help button on space/item pages if the user visited a marketing page first.
      // Instead, survey auto-display is controlled via display conditions in PostHog.
      // The Help button explicitly calls displaySurvey() with ignoreConditions: true.
      // Enable session recordings
      disable_session_recording: false,
      // Capture pageviews manually for SPA navigation
      capture_pageview: false,
      // Enable autocapture for better analytics
      autocapture: true,
      // Capture performance metrics
      capture_performance: true,
      // Person profiles only for identified users
      person_profiles: "identified_only",
      // Enable debug mode in development
      debug: import.meta.env.DEV,
      // Privacy settings - mask sensitive data
      mask_all_text: false,
      // UTM tracking and marketing analytics
      capture_pageleave: true,
      // Honour an EXPLICIT decline, and only that. This is the first
      // consent enforcement the app has ever had: the two
      // `autocapture_opt_out_capturing*` options that used to sit here
      // are not posthog-js config keys at all (the real one is
      // `autocapture_opt_out`), and both consent UIs called
      // opt_in/opt_out on the never-assigned `window.posthog` — so
      // nothing was gated and declining did nothing.
      //
      // Deliberately `consent === "false"`, NOT `!hasConsent`: a consent
      // banner renders only on marketing (`layouts/marketing.html.erb`)
      // and the creator dashboard (`layouts/persistent-layout.tsx`).
      // Listener pages — `listener.html.erb` / `public.html.erb`, where
      // playback happens — have never shown one, so those users sit at
      // `null` forever. Opting out on `null` would silence every
      // listener: no content_play (this file's whole purpose), and no
      // pageviews, autocapture or session replay either. Whether the
      // never-asked population should be opted out is a product/legal
      // call that needs listener-side consent UI first (#1765).
      opt_out_capturing_by_default: consent === "false",
      // Suppress irrelevant selectors to keep analytics clean
      autocapture_disable_selector: [
        // Navigation elements that don't provide value
        "nav",
        "header",
        "footer",
        // Form elements that are too noisy
        'input[type="search"]',
        'input[type="email"]',
        'input[type="text"]:not([name*="title"]):not([name*="name"])',
        // UI elements that don't indicate user intent
        ".btn-secondary",
        ".btn-outline",
        ".close",
        ".cancel",
        // Loading states and transitions
        ".loading",
        ".spinner",
        ".skeleton",
        // Developer tools and debug elements
        '[data-testid*="debug"]',
        '[class*="debug"]',
        // Common noise selectors
        ".sr-only",
        ".visually-hidden",
        '[aria-hidden="true"]',
      ],
      // Custom properties
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      loaded: (posthogInstance: any) => {
        if (import.meta.env.DEV) {
          console.log("PostHog loaded callback fired", {
            hasPosthogInstance: !!posthogInstance,
            isMarketing,
            hasPosthogResolve: !!posthogResolve,
          })

          if (isMarketing) {
            console.log(
              "PostHog on marketing page (surveys still enabled for programmatic display)",
            )
          }
        }

        // The callback parameter is the initialized instance; fall back to
        // the imported module symbol, never to a global nothing assigns.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const instance = posthogInstance ?? posthog

        // Resolve the ready Promise when PostHog loads
        if (posthogResolve && instance) {
          if (import.meta.env.DEV) {
            console.log(
              "getPostHogReady: Resolving promise via loaded callback",
            )
          }
          // Clear timeout since we're resolving successfully
          if (posthogTimeoutId) {
            clearTimeout(posthogTimeoutId)
            posthogTimeoutId = null
          }
          posthogResolve(instance as typeof posthog)
          posthogResolve = null
        } else if (instance) {
          if (import.meta.env.DEV) {
            console.log(
              "getPostHogReady: No resolver, creating resolved promise with instance",
            )
          }
          // Create resolved Promise if getPostHogReady wasn't called yet
          posthogReadyPromise ??= Promise.resolve(instance as typeof posthog)
        } else {
          if (import.meta.env.DEV) {
            console.error(
              "getPostHogReady: no instance available in loaded callback",
            )
          }
        }

        // Capture UTM parameters and referrer information
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        captureMarketingData(posthogInstance)

        // Register in-app browser properties as super properties
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        registerIabProperties(posthogInstance)

        // Note: Pageview tracking is handled by application.ts (Turbo) and inertia.ts (Inertia)
        // to avoid duplicate tracking. The loaded callback only handles marketing data capture.
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any)
  } catch (error) {
    // If posthog.init() throws, reject the promise to prevent permanent hang
    const initError =
      error instanceof Error
        ? error
        : new Error(`PostHog initialization failed: ${String(error)}`)
    if (posthogResolve) {
      // Clear timeout since we're rejecting due to init error
      if (posthogTimeoutId) {
        clearTimeout(posthogTimeoutId)
        posthogTimeoutId = null
      }
      // We can't reject an already-created Promise directly, but we can replace it
      posthogResolve = null
    }
    // A transient init failure must not latch: leaving a rejected promise
    // cached would silently drop every product event for the rest of the
    // session, since getPostHogReady hands the same promise to every
    // caller. Clearing it lets the next capture retry initialization.
    clearFailedReadyPromise()
    console.error("PostHog initialization error:", initError)
    return
  }

  // Set up Inertia navigation tracking (intentionally not awaited to avoid blocking)
  void setupNavigationTracking()

  return posthog
}

// Set up navigation tracking for InertiaJS
async function setupNavigationTracking() {
  // Only set up Inertia router tracking on non-marketing pages
  if (isMarketingPage()) {
    if (import.meta.env.DEV) {
      console.log(
        "Marketing page detected, skipping Inertia navigation tracking",
      )
    }
    return
  }

  try {
    // Dynamically import router only when needed (on Inertia pages)
    const { router } = await import("@inertiajs/react")

    if (!router || typeof router.on !== "function") {
      if (import.meta.env.DEV) {
        console.log("Inertia router not available")
      }
      return
    }

    // Track page views only on successful navigation to avoid duplication
    router.on("success", (event) => {
      const page = event.detail.page
      const url = page.url
      const { pathname, search, hash } = parseUrl(url)

      trackPageView(page.component, {
        url,
        $pathname: pathname,
        $search: search,
        $hash: hash,
        props: page.props,
      })
    })
  } catch (error) {
    if (import.meta.env.DEV) {
      console.log("Failed to load Inertia router:", error)
    }
  }
}

// posthog-js is imported as a module here, so nothing ever assigns
// `window.posthog` — only the snippet loader does that. Capture sites that
// guarded on it were therefore permanent no-ops: `content_play` never fired
// once in 180 days of production (#1763).
//
// Captures queue behind the init-ready promise, which is resolved by
// posthog.init's `loaded` callback with the real instance. Deferring (vs
// calling posthog.capture directly and relying on the SDK's own pre-init
// buffering) keeps one delivery policy for every product event.
function captureEvent(event: string, properties: Record<string, unknown>) {
  void getPostHogReady()
    .then((instance) => {
      instance.capture(event, properties)
      if (import.meta.env.DEV) {
        // Deliberately not the whole bag — for $pageview it carries the
        // entire Inertia page payload.
        console.log(`PostHog ${event} tracked`, {
          item_id: properties.item_id,
          page: properties.page,
        })
      }
    })
    .catch((error) => {
      // Analytics must never break the surface it measures.
      if (import.meta.env.DEV) {
        console.error(`Error tracking ${event}:`, error)
      }
    })
}

// Track page views for SPA navigation
export function trackPageView(page: string, props?: Record<string, unknown>) {
  // Capture URL values at call time to ensure consistency
  const url = window.location.href
  const pathname = window.location.pathname
  const search = window.location.search
  const hash = window.location.hash

  captureEvent("$pageview", {
    $current_url: url,
    $pathname: pathname,
    $search: search,
    $hash: hash,
    page,
    environment: currentEnvironment(),
    ...props,
  })
}

// Identify user after login
export function identifyUser(
  userId: string,
  userProperties?: Record<string, unknown>,
) {
  posthog.identify(userId, userProperties)
}

// Reset user on logout.
//
// Guarded on `_isIdentified()` because `reset()` mints a brand new anonymous
// distinct id. Callers run on every signed-out page load, so an unguarded reset
// would hand a genuinely anonymous visitor a fresh id per pageview — breaking
// anonymous session continuity and the anonymous -> identified merge. Resetting
// only on the identified -> signed-out transition is what actually stops the
// previous user's identity from following the browser.
export function resetUser() {
  if (posthog._isIdentified()) {
    posthog.reset()
  }
}

// Core product event tracking functions
export function trackContentPlay(
  itemId: string,
  itemTitle: string,
  spaceId: string,
  spaceUrl: string,
  contentType: "audio" | "video" = "audio",
  containerId?: string,
) {
  const properties: Record<string, unknown> = {
    item_id: itemId,
    item_title: itemTitle,
    space_id: spaceId,
    space_url: spaceUrl,
    content_type: contentType,
    timestamp: new Date().toISOString(),
    environment: currentEnvironment(),
  }
  if (containerId) {
    properties.container_id = containerId
  }
  captureEvent("content_play", properties)
}

export function trackUnlockSuccess(
  itemId: string,
  itemTitle: string,
  spaceId: string,
  spaceUrl: string,
  unlockType: "free" | "purchase" = "free",
) {
  captureEvent("unlock_success", {
    item_id: itemId,
    item_title: itemTitle,
    space_id: spaceId,
    space_url: spaceUrl,
    unlock_type: unlockType,
    timestamp: new Date().toISOString(),
    environment: currentEnvironment(),
  })
}

export function trackPurchaseComplete(
  itemId: string,
  itemTitle: string,
  spaceId: string,
  spaceUrl: string,
  price: number,
  currency = "USD",
) {
  captureEvent("purchase_complete", {
    item_id: itemId,
    item_title: itemTitle,
    space_id: spaceId,
    space_url: spaceUrl,
    price: price,
    currency: currency,
    timestamp: new Date().toISOString(),
    environment: currentEnvironment(),
  })
}

export function trackStripeCheckoutOpened(paymentType: string) {
  captureEvent("stripe_checkout_opened", {
    payment_type: paymentType,
    timestamp: new Date().toISOString(),
    environment: currentEnvironment(),
  })
}

// `ref` names the placement (dashboard, faq, founder, …) so bookings can be
// attributed to the surface that produced them (#1625).
export function trackSetupCallInviteClicked(ref: string) {
  captureEvent("setup_call_invite_clicked", {
    ref,
    timestamp: new Date().toISOString(),
    environment: currentEnvironment(),
  })
}

// Register in-app browser detection as super properties so every event carries them
function registerIabProperties(posthogInstance: typeof posthog) {
  if (typeof document === "undefined") return
  if (!isPostHogInstance(posthogInstance)) return

  const iabMeta = document.querySelector('meta[name="is-iab"]')
  const isIab = iabMeta?.getAttribute("content") === "true"
  const iabSource =
    document
      .querySelector('meta[name="iab-source"]')
      ?.getAttribute("content") ?? null

  posthogInstance.register_once({
    is_iab: isIab,
    iab_source: iabSource,
  })
}

// Capture UTM parameters and marketing data
function captureMarketingData(instance: typeof posthog) {
  if (typeof window === "undefined") return

  const urlParams = new URLSearchParams(window.location.search)
  const marketingData: Record<string, unknown> = {}

  // Capture UTM parameters
  const utmParams = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "utm_id",
    "utm_source_platform",
    "utm_creative_format",
    "utm_marketing_tactic",
  ]

  utmParams.forEach((param) => {
    const value = urlParams.get(param)
    if (value) {
      marketingData[param] = value
    }
  })

  // Capture referrer information
  if (document.referrer) {
    marketingData.referrer = document.referrer
    marketingData.referrer_domain = new URL(document.referrer).hostname
  }

  // Capture landing page information
  marketingData.landing_page = window.location.pathname
  marketingData.landing_page_full = window.location.href

  // Capture device and browser info
  marketingData.user_agent = navigator.userAgent
  marketingData.screen_resolution = `${screen.width}x${screen.height}`
  marketingData.viewport_size = `${window.innerWidth}x${window.innerHeight}`

  // Set marketing properties on PostHog person
  if (Object.keys(marketingData).length > 0) {
    if (isPostHogInstance(instance)) {
      instance.people.set(marketingData)

      // Also capture as a marketing event
      instance.capture("marketing_data_captured", {
        ...marketingData,
        environment: currentEnvironment(),
      })
    }
  }
}

// These carried the identical `window.posthog` guard as the capture sites,
// so every JS-side flag read as off and onFeatureFlags never registered.
// No callers today, which is the only reason it never bit — but a flag
// that silently reads false is a trap worth disarming, not leaving armed.
export function isFeatureEnabled(key: string): boolean {
  return posthog.isFeatureEnabled(key) ?? false
}

export function getFeatureFlag(key: string): string | boolean | undefined {
  return posthog.getFeatureFlag(key)
}

export function onFeatureFlags(
  callback: (
    flags: string[],
    variants: Record<string, string | boolean>,
  ) => void,
): () => void {
  return posthog.onFeatureFlags(callback)
}

// Export posthog instance for direct use
export { posthog }
